import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import axios from 'axios';
import { UserEntity, CreditHistory } from '../models/entity/userEntity';
import { UserService } from '../services/userService';
import { track } from '../third-party/MixPanel';
import { sessionService } from '../services/SessionService';
import mixpanel from 'mixpanel-browser';
import { _getReadings } from '../services/ReadingService';
import { journalService } from '../services/JournalService';
import { storeInLocalStorage } from '../services/LocalStorageService';
import { RNBridgeContext } from './RNBridgeContext';

interface AuthContextProps {
  isAuthenticated: boolean;
  user: UserEntity | null;
  login: (token: string) => Promise<void>;
  logout: () => void;
  error: string | null;
  isDevVersion: boolean;

  useCredits: (amount: number) => Promise<void>;
  addCredits: (amount: number) => Promise<void>;
  refreshUserData: () => Promise<UserEntity | undefined>;
  onboardUser: (data: any) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  loginWithCredentials: (email: string, password: string) => Promise<void>;
  getFavoriteReadings: () => Promise<Array<{ type: string, count: number }> | undefined>;
  updateUserLanguage: (language: string) => Promise<void>;
}

const axiosInstance = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL}`,
  headers: {
    'Content-Type': 'application/json',
  },
});

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const token: string | null = localStorage.getItem('token');
  const [isAuthenticated, setIsAuthenticated] = useState(!!token);
  const [user, setUser] = useState<UserEntity | null>(
    UserEntity.getFromLocalStorage()
  );
  const [error, setError] = useState<string | null>(null);
  const { sendMessage } = useContext(RNBridgeContext);

  const isDevVersion = user?.email === "test@test.com";

  const updateUser = async (user: UserEntity) => {
    setUser(user);
    localStorage.setItem('user', user.toString());
    localStorage.setItem('dailyReading', JSON.stringify(user.dailyReading));
  }

  // Helper function to clear all user data from storage
  // This ensures that when a user logs out or their session expires,
  // no sensitive user data remains in localStorage or sessionStorage
  // This is important for security and privacy compliance
  const clearAllUserData = () => {
    // Clear all localStorage data that might contain user-specific information
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('onboarding');
    // Keep appLanguage to persist language preference across sessions
    localStorage.removeItem('readingMetadata');
    localStorage.removeItem('selectedReading');
    localStorage.removeItem('drawnCards');
    localStorage.removeItem('dailyRandomCards');
    localStorage.removeItem('spreadType');
    localStorage.removeItem('formData');
    localStorage.removeItem('selectedReadingCategory');
    localStorage.removeItem('journalDraft');
    localStorage.removeItem('foodList'); // From LocalStorageService
    localStorage.removeItem('app_session'); // From SessionService



    // Clear any other potential user data keys that might exist
    // This ensures no user-specific data is left behind
    const keysToRemove = [
      'user_', 'reading_', 'card_', 'session_', 'auth_', 'profile_',
      'preference_', 'setting_', 'history_', 'cache_', 'temp_',
      'tracking_', 'analytics_', 'mixpanel_', 'mp_', 'ga_', 'gtag_'
    ];

    // Remove any localStorage keys that start with these patterns
    Object.keys(localStorage).forEach(key => {
      if (keysToRemove.some(pattern => key.startsWith(pattern))) {
        localStorage.removeItem(key);
      }
    });

    // Clear sessionStorage data
    sessionStorage.clear();

    // Clear Mixpanel tracking data and reset user identification
    // Mixpanel stores data in localStorage with keys like mp_*, so we need to clear those
    // This ensures that user tracking data is completely removed and no analytics events
    // are associated with the logged-out user
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('mp_')) {
        localStorage.removeItem(key);
      }
    });

    // Reset Mixpanel user identification and clear super properties if possible
    try {
      if (mixpanel && typeof mixpanel.identify === 'function') {
        mixpanel.identify(''); // Clear user identification
      }

      // Clear any super properties that might have been set
      if (mixpanel && typeof mixpanel.register === 'function') {
        mixpanel.register({}); // Clear super properties
      }

      // Clear people properties if available
      if (mixpanel && mixpanel.people && typeof mixpanel.people.set === 'function') {
        mixpanel.people.set({}); // Clear people properties
      }
    } catch (error) {
      console.warn('Could not reset Mixpanel data:', error);
    }
  }

  const login = async (token: string) => {
    if (token && !localStorage.getItem('token')) {
      localStorage.setItem('token', token);
      setIsAuthenticated(true);
      setUser(UserEntity.getFromLocalStorage());
    }

    try {
      const currentUser = await UserService.getCurrentUser(token);
      updateUser(currentUser);
      processUser(currentUser);
      setError(null);

      // Start a new session for authenticated user after successful login
      sessionService.startSessionAfterLogin();

      // Force call get readings and journal data after successful login
      try {
        // Load readings data
        const readingsData = await _getReadings();
        storeInLocalStorage('readings', readingsData);
        console.log('Readings loaded and stored successfully after login');

        // Load journal entries data
        const journalsData = await journalService.getAllEntries();
        storeInLocalStorage('journals', journalsData);
        console.log('Journals loaded and stored successfully after login');
      } catch (dataError) {
        console.warn('Could not load readings/journals after login:', dataError);
        // Don't fail the login if data loading fails
      }

      console.log('User logged in successfully, session started');
    } catch (err) {
      setError('Invalid token');
      setIsAuthenticated(false);

      // Clear all localStorage data when login fails
      clearAllUserData();

      // End session on failed login
      sessionService.forceEndSession();
    }
  };

  const logout = () => {
    // End current session before clearing auth data
    sessionService.forceEndSession();

    setIsAuthenticated(false);
    setUser(null);

    // Clear all user data from storage
    clearAllUserData();



    // Temporarily ignore unload events during navigation to prevent double session end
    sessionService.temporarilyIgnoreUnloadEvents(3000);

    // Use React Router navigation instead of window.location.href to avoid page reload
    // Note: This requires the logout function to be called from within a component that has access to navigate
    // For now, keeping window.location.href but the session is already ended above
    window.location.href = '/login';
  };
  const getFavoriteReadings = async () => {
    console.log('user', user);
    return user?.favoriteReadings;
  };

  const refreshUserData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    try {
      let currentUser = await UserService.getCurrentUser(token);
      updateUser(currentUser);
      processUser(currentUser);

      // Update language in localStorage if user profile
      localStorage.setItem('appLanguage', currentUser.language);

      return currentUser;
    } catch (err: any) {
      console.error('Error refreshing user data:', err);
      // If we get a 401 error, the token is invalid/expired
      if (err?.response?.status === 401 || err?.status === 401) {
        setError('Session expired. Please login again.');
        setIsAuthenticated(false);

        // Clear all localStorage data when authentication fails
        clearAllUserData();

        // End the current session when auth fails
        sessionService.forceEndSession();



        // Redirect to login page
        //window.location.href = '/login';
      }
    }
  };
  const processUser = (user: UserEntity) => {
    localStorage.setItem('onboarding', user.onboarding.toString());
    if (!user.onboarding) {
      if (window.location.pathname !== '/onboarding') {
        // Temporarily ignore unload events during onboarding navigation
        sessionService.temporarilyIgnoreUnloadEvents(3000);
        window.location.href = '/onboarding';
      }
    }
  }

  const useCredits = async (amount: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const updatedUser = await UserService.useCredits(token, amount);
      updateUser(updatedUser);
    } catch (error) {
      console.error('Error using credits:', error);
      throw new Error(error instanceof Error ? error.message : 'Error using credits');
    }
  };
  const onboardUser = async (data: any) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const updatedUser = await UserService.onboardUser(token, data);
      updateUser(updatedUser);
    } catch (error) {
      console.error('Error onboarding user:', error);
      throw new Error(error instanceof Error ? error.message : 'Error onboarding user');
    }
  };

  const addCredits = async (amount: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const updatedUser = await UserService.addCredits(token, amount);
      updateUser(updatedUser);
    } catch (error) {
      console.error('Error adding credits:', error);
      throw new Error(error instanceof Error ? error.message : 'Error adding credits');
    }
  };

  const loginWithCredentials = async (email: string, password: string) => {
    try {
      const response = await axiosInstance.post(
        `/api/auth/login`,
        {
          email,
          password,
        }
      );

      await login(response.data.token);
      track('login_success', { method: 'email' });
    } catch (err: any) {
      console.error('Login error:', err);
      throw new Error('Invalid credentials, please try again.');
    }
  };

  const requestPasswordReset = async (email: string) => {
    try {
      await UserService.requestPasswordReset(email);
    } catch (error) {
      console.error('Error requesting password reset:', error);
      throw new Error('Failed to request password reset');
    }
  };

  const resetPassword = async (token: string, newPassword: string) => {
    try {
      await UserService.resetPassword(token, newPassword);
    } catch (error) {
      console.error('Error resetting password:', error);
      throw new Error('Failed to reset password');
    }
  };

  const updateUserLanguage = async (language: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      const updatedUser = await UserService.updateProfile(token, { language });
      updateUser(updatedUser);
    } catch (error) {
      console.error('Error updating user language:', error);
      throw new Error(error instanceof Error ? error.message : 'Error updating user language');
    }
  };

  useEffect(() => {
    if (token && isAuthenticated) {
      const interval = setInterval(refreshUserData, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
    // Only call refreshUserData if we have a token and are authenticated
    if (token && isAuthenticated) {
      refreshUserData();
    }
  }, [token, isAuthenticated]);

  // Send user ID and profile to React Native app when user is authenticated
  // This is critical for RevenueCat webhook to attribute purchases to the correct user
  useEffect(() => {
    if (isAuthenticated && user?._id) {
      if (sendMessage) {
        console.log('📱 [AuthContext] Sending user ID and profile to React Native app:', {
          userId: user._id,
          email: user.email,
          firstName: user.firstname,
          lastName: user.lastname
        });
        sendMessage({
          action: 'setUserId',
          type: 'SET_USER_ID',
          payload: user._id,
          userId: user._id,
          userProfile: {
            email: user.email,
            firstName: user.firstname,
            lastName: user.lastname
          }
        });
        console.log('📱 [AuthContext] User ID and profile sent successfully');
      } else {
        console.warn('⚠️  [AuthContext] sendMessage not available - user ID not sent to mobile app');
      }
    } else {
      console.log('📱 [AuthContext] Not sending user ID:', {
        isAuthenticated,
        hasUserId: !!user?._id,
        hasSendMessage: !!sendMessage
      });
    }
  }, [isAuthenticated, user?._id, user?.email, user?.firstname, user?.lastname, sendMessage]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        login,
        logout,
        error,
        isDevVersion,
        useCredits,
        addCredits,
        refreshUserData,
        onboardUser,
        requestPasswordReset,
        resetPassword,
        loginWithCredentials,
        getFavoriteReadings,
        updateUserLanguage
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useCredits = () => {
  const context = useAuth();
  return {
    useCredits: context.useCredits,
    addCredits: context.addCredits,
  };
};
